import { useEffect, useMemo, useState, useRef, FormEvent } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogClose,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Search, Ellipsis, X } from "lucide-react";
import { toast } from "sonner";
import { activityService } from "@/services/activity";
import {
  Activity,
  ActivityStatu,
  ActivityStatus,
  STATUS_OPTIONS,
} from "@/types/activity";
import { Label } from "@/components/ui/label";
import { Commodity, CommodityCommitedByActivity } from "@/types/commodities";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";

// --- MODIFIED: ProductInForm Type Definition ---
// 1. 从原始类型中剔除我们将要覆盖的字段
// 2. 明确地将价格字段定义为字符串，以匹配表单状态
type ProductInForm = Omit<
  Partial<CommodityCommitedByActivity>,
  "activity_price" | "gift_price"
> & {
  _isGiftEnabled?: boolean;
  gift_duration?: number;
  gift_unit?: string;
  activity_price?: string; // 明确为字符串
  gift_price?: string; // 明确为字符串
};

const GIFT_UNIT_OPTIONS = [
  { value: "day", label: "天" },
  { value: "month", label: "月" },
  { value: "year", label: "年" },
];

const formatPriceToYuan = (priceInCents: number | undefined) =>
  (priceInCents || 0) / 100;
const formatPriceToCents = (priceInYuan: number | string | undefined) =>
  Math.round(Number(priceInYuan || 0) * 100);

export default function ActivitiesManagement() {
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);

  const [loading, setLoading] = useState(true);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [total, setTotal] = useState(0);

  const [status, setStatus] = useState<string>("all");

  const [selectedActivity, setSelectedActivity] =
    useState<Partial<Activity> | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);

  const [statusInForm, setStatusInForm] = useState<
    ActivityStatus | undefined
  >();

  const [productsInForm, setProductsInForm] = useState<ProductInForm[]>([]);
  const [allProducts, setAllProducts] = useState<Commodity[]>([]);

  const formRef = useRef<HTMLFormElement>(null);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(total / pageSize)),
    [total, pageSize]
  );

  const buildRequestParams = () => {
    const params: any = { page, page_size: pageSize };
    if (status !== "all") params.status = status;
    return params;
  };

  const fetchActivities = async () => {
    setLoading(true);
    try {
      const params = buildRequestParams();
      const res = await activityService.getActivities(params);
      setActivities(res.list ?? []);
      setTotal(res.total ?? 0);
    } catch (e) {
      console.error(e);
      toast.error("获取活动列表失败", {
        description: e instanceof Error ? e.message : "请稍后重试",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchProductions = async () => {
    try {
      const res = await activityService.getSelectableProducts();
      const list = res.list ?? [];
      if (list.length === 0) {
        toast.warning("暂无可关联的商品", {
          description: "请先在商品管理中新增“使用中”的商品",
        });
      }
      setAllProducts(list);
    } catch (e) {
      console.error(e);
      toast.error("获取商品列表失败", {
        description: e instanceof Error ? e.message : "请稍后重试",
      });
      onDialogClose(false);
    }
  };

  const handleSearch = () => {
    setPage(1);
    fetchActivities();
  };

  const formatDateTime = (dateStr?: string) => {
    if (!dateStr || dateStr === "0001-01-01T00:00:00Z") return "-";
    return new Date(dateStr).toLocaleString();
  };

  const handleCreateActivity = () => {
    setSelectedActivity({});
    setStatusInForm(undefined);
    setProductsInForm([]);
    setDetailDialogOpen(true);
    fetchProductions();
  };

  const viewActivityDetails = (activity: Activity) => {
    setSelectedActivity({ ...activity });
    setStatusInForm(activity.status);
    const productsForForm: ProductInForm[] = (activity.products || []).map(
      (p) => ({
        ...p,
        _isGiftEnabled: p.gift_type !== 0,
        activity_price: String(formatPriceToYuan(p.activity_price)),
        gift_price: String(formatPriceToYuan(p.gift_price)),
      })
    );
    setProductsInForm(productsForForm);
    setDetailDialogOpen(true);
    fetchProductions();
  };

  const onFormChange = (label: keyof Activity, value: any) => {
    if (label === "status") setStatusInForm(value as ActivityStatus);
    setSelectedActivity((prev) => ({ ...prev, [label]: value }));
  };

  const formSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!selectedActivity) return;

    // 验证有效期
    if (!selectedActivity.start_time || !selectedActivity.end_time) {
      toast.warning("请设置活动有效期");
      return;
    }

    // 验证结束时间必须大于开始时间
    if (
      new Date(selectedActivity.start_time) >=
      new Date(selectedActivity.end_time)
    ) {
      toast.warning("结束时间必须大于开始时间");
      return;
    }

    if (!productsInForm || productsInForm.length === 0) {
      toast.warning("请至少关联一个商品");
      return;
    }

    const cleanedProducts = productsInForm.map((p) => {
      const { _isGiftEnabled, ...rest } = p;
      return {
        ...rest,
        activity_price: formatPriceToCents(p.activity_price),
        gift_type: _isGiftEnabled ? 1 : 0,
        gift_name: _isGiftEnabled ? p.gift_name : "",
        gift_price: _isGiftEnabled ? formatPriceToCents(p.gift_price) : 0,
        gift_duration: _isGiftEnabled ? p.gift_duration || 0 : 0,
        gift_unit: _isGiftEnabled ? p.gift_unit || "" : "",
      };
    });

    const payload = { ...selectedActivity, products: cleanedProducts };

    try {
      if (payload.id) {
        await activityService.updateActivityStatus(payload as Activity);
        toast.success("活动更新成功");
      } else {
        await activityService.createActivity(payload as Activity);
        toast.success("活动创建成功");
      }
      fetchActivities();
      onDialogClose(false);
    } catch (err) {
      console.error(err);
      toast.error("操作失败", {
        description: err instanceof Error ? err.message : "请检查表单内容",
      });
    }
  };

  const onDialogClose = (open: boolean) => {
    if (!open) {
      setDetailDialogOpen(false);
      setSelectedActivity(null);
      setStatusInForm(undefined);
      setProductsInForm([]);
    }
  };

  // --- MODIFIED: onRelatedNewProduct ---
  // 确保创建的对象符合新的 ProductInForm 类型
  const onRelatedNewProduct = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (allProducts.length === 0) {
      toast.error("没有可供选择的商品", {
        description: "无法添加新的关联商品",
      });
      return;
    }
    const firstProduct = allProducts[0];
    const newProduct: ProductInForm = {
      product_id: firstProduct.id,
      original_price: firstProduct.price,
      activity_price: String(formatPriceToYuan(firstProduct.price)), // 字符串
      _isGiftEnabled: false,
      gift_type: 0,
      gift_name: "",
      gift_price: "0", // 字符串
      gift_duration: 0,
      gift_unit: "year",
    };
    setProductsInForm((prev) => [...prev, newProduct]);
  };

  // --- MODIFIED: handleProductFieldChange ---
  // 添加对数字字段的特殊处理
  const handleProductFieldChange = (
    index: number,
    field: keyof ProductInForm,
    value: any
  ) => {
    if (field === "activity_price" || field === "gift_price") {
      const regex = /^\d*(\.\d{0,2})?$/;
      if (value && !regex.test(value)) {
        return;
      }
    }

    if (field === "gift_duration") {
      const regex = /^\d*$/;
      if (value && !regex.test(value)) {
        return;
      }
    }

    const newProducts = [...productsInForm];
    const productToUpdate = { ...newProducts[index] };

    // 对需要为数字的字段进行转换
    if (field === "gift_duration") {
      (productToUpdate as any)[field] =
        value === "" ? undefined : Number(value);
    } else {
      (productToUpdate as any)[field] = value;
    }

    newProducts[index] = productToUpdate;
    setProductsInForm(newProducts);
  };

  const handleProductChange = (index: number, newProductId: string) => {
    const selectedProd = allProducts.find((p) => p.id === newProductId);
    if (!selectedProd) return;

    const newProducts = [...productsInForm];
    const productToUpdate = { ...newProducts[index] };

    productToUpdate.product_id = selectedProd.id;
    productToUpdate.original_price = selectedProd.price;
    productToUpdate.activity_price = String(
      formatPriceToYuan(selectedProd.price)
    ); // 字符串

    newProducts[index] = productToUpdate;
    setProductsInForm(newProducts);
  };

  const handleDeleteProduct = (index: number) => {
    setProductsInForm((prev) => prev.filter((_, i) => i !== index));
  };

  const resetFilters = () => {
    setStatus("all");
    setPage(1);
    fetchActivities();
  };

  useEffect(() => {
    fetchActivities();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize]);

  return (
    <div className="space-y-4 h-full flex flex-col">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold tracking-tight">活动管理</h2>
      </div>
      <div className="rounded-lg border p-4 space-y-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="flex flex-col gap-2">
            <label className="text-sm text-gray-600">状态</label>
            <Select
              value={status}
              onValueChange={(val: string) => setStatus(val)}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="选择状态" />
              </SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-2 justify-end">
            <label className="text-sm text-gray-600 opacity-0">操作</label>
            <div className="flex gap-2 justify-end">
              <Button
                className="w-24"
                variant="outline"
                onClick={handleCreateActivity}
              >
                新增活动
              </Button>
              <Button className="w-24" variant="outline" onClick={resetFilters}>
                重置
              </Button>
              <Button className="w-24" onClick={handleSearch}>
                <Search className="mr-2 h-4 w-4" />
                查询
              </Button>
            </div>
          </div>
        </div>
      </div>
      <div className="rounded-md border overflow-auto max-h-[60vh]">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="whitespace-nowrap">活动名称</TableHead>
              <TableHead className="whitespace-nowrap">活动编码</TableHead>
              <TableHead className="whitespace-nowrap">关联商品</TableHead>
              <TableHead className="whitespace-nowrap">活动状态</TableHead>
              <TableHead className="whitespace-nowrap">活动介绍</TableHead>
              <TableHead className="whitespace-nowrap">创建日期</TableHead>
              <TableHead className="whitespace-nowrap">操作</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} className="h-24 text-center">
                  <div className="flex justify-center items-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900"></div>
                  </div>
                </TableCell>
              </TableRow>
            ) : activities.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="h-24 text-center">
                  没有找到活动记录
                </TableCell>
              </TableRow>
            ) : (
              activities.map((activity) => (
                <TableRow key={activity.id}>
                  <TableCell className="max-w-[150px] truncate">
                    {activity.name}
                  </TableCell>
                  <TableCell className="max-w-[150px] truncate">
                    {activity.code}
                  </TableCell>
                  <TableCell className="max-w-[200px]">
                    {activity.products?.map((product) => (
                      <div key={product.id} className="w-fit mb-2 last:mb-0">
                        <div className="flex items-center">
                          <div className="mr-2 text-red-600">
                            ￥{formatPriceToYuan(product.activity_price)}
                          </div>
                          <div className="line-through text-gray-500 text-xs">
                            (￥{formatPriceToYuan(product.original_price)})
                          </div>
                        </div>
                        {product.gift_type !== 0 && (
                          <div className="text-xs text-gray-600 pl-1">
                            赠送:{" "}
                            {product.gift_name ||
                              `赠品类型 ${product.gift_type}`}
                          </div>
                        )}
                      </div>
                    ))}
                  </TableCell>
                  <TableCell>
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium ${
                        activity.status === "active"
                          ? "bg-green-100 text-green-800"
                          : activity.status === "inactive"
                          ? "bg-red-100 text-red-800"
                          : activity.status === "draft"
                          ? "bg-yellow-100 text-yellow-800"
                          : "bg-gray-100 text-gray-800"
                      }`}
                    >
                      {ActivityStatu[activity.status]}
                    </span>
                  </TableCell>
                  <TableCell
                    className="max-w-[150px] truncate"
                    title={activity.description}
                  >
                    {activity.description}
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    {formatDateTime(activity.createdAt)}
                  </TableCell>
                  <TableCell>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => viewActivityDetails(activity)}
                          >
                            <Ellipsis className="h-4 w-4" />
                          </Button>
                        </TooltipTrigger>
                      </Tooltip>
                    </TooltipProvider>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {selectedActivity && (
        <Dialog
          open={detailDialogOpen}
          onOpenChange={onDialogClose}
          key={selectedActivity.id || "new-activity"}
        >
          <DialogContent className="sm:max-w-3xl bg-white">
            <DialogHeader>
              <DialogTitle className="text-center">
                {selectedActivity.id ? "活动详情" : "新增活动"}
              </DialogTitle>
            </DialogHeader>
            <form
              ref={formRef}
              onSubmit={formSubmit}
              className="max-h-[70vh] overflow-y-auto p-1 pr-4 space-y-6"
            >
              <div className="space-y-4">
                <div className="grid grid-cols-6 items-center gap-4">
                  <Label htmlFor="name" className="text-right">
                    活动名称<span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="name"
                    defaultValue={selectedActivity.name}
                    className="col-span-5"
                    required
                    autoComplete="off"
                    onChange={(e) => onFormChange("name", e.target.value)}
                  />
                </div>
                <div className="grid grid-cols-6 items-center gap-4">
                  <Label htmlFor="cover" className="text-right">
                    活动封面
                  </Label>
                  <Input
                    id="cover"
                    placeholder="请输入封面图片URL"
                    defaultValue={selectedActivity.cover}
                    className="col-span-5"
                    autoComplete="off"
                    onChange={(e) => onFormChange("cover", e.target.value)}
                  />
                </div>

                <div className="grid grid-cols-6 items-center gap-4">
                  <Label htmlFor="description" className="text-right">
                    活动介绍
                  </Label>
                  <Textarea
                    id="description"
                    defaultValue={selectedActivity.description}
                    className="col-span-5"
                    onChange={(e) =>
                      onFormChange("description", e.target.value)
                    }
                  />
                </div>

                <div className="grid grid-cols-6 items-center gap-4">
                  <Label htmlFor="start_time" className="text-right">
                    有效期<span className="text-red-500">*</span>
                  </Label>
                  <div className="col-span-5 flex items-center gap-2">
                    <div className="relative w-full">
                      <Input
                        id="start_time"
                        type={selectedActivity.start_time ? "date" : "text"}
                        placeholder="请选择开始日期"
                        onFocus={(e) => (e.target.type = "date")}
                        onBlur={(e) => {
                          if (!e.target.value) {
                            e.target.type = "text";
                          }
                        }}
                        value={
                          selectedActivity.start_time
                            ? selectedActivity.start_time.split("T")[0]
                            : ""
                        }
                        onChange={(e) => {
                          const value = e.target.value;
                          onFormChange(
                            "start_time",
                            value
                              ? new Date(value + "T00:00:00.000Z").toISOString()
                              : ""
                          );
                        }}
                      />
                      {selectedActivity.start_time && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 text-gray-400 hover:text-gray-700"
                          onClick={() => onFormChange("start_time", "")}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                    <span className="text-gray-500">-</span>
                    <div className="relative w-full">
                      <Input
                        type={selectedActivity.end_time ? "date" : "text"}
                        placeholder="请选择结束日期"
                        onFocus={(e) => (e.target.type = "date")}
                        onBlur={(e) => {
                          if (!e.target.value) {
                            e.target.type = "text";
                          }
                        }}
                        value={
                          selectedActivity.end_time
                            ? selectedActivity.end_time.split("T")[0]
                            : ""
                        }
                        onChange={(e) => {
                          const value = e.target.value;
                          onFormChange(
                            "end_time",
                            value
                              ? new Date(value + "T23:59:59.999Z").toISOString()
                              : ""
                          );
                        }}
                      />
                      {selectedActivity.end_time && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 text-gray-400 hover:text-gray-700"
                          onClick={() => onFormChange("end_time", "")}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-6 items-center gap-4">
                  <Label htmlFor="status" className="text-right">
                    状态<span className="text-red-500">*</span>
                  </Label>
                  <div className="col-span-5">
                    <Select
                      value={statusInForm}
                      onValueChange={(val: string) =>
                        onFormChange("status", val)
                      }
                      required
                    >
                      <SelectTrigger>
                        <SelectValue id="status" placeholder="选择状态" />
                      </SelectTrigger>
                      <SelectContent>
                        {STATUS_OPTIONS.slice(1).map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
              <div className="space-y-4">
                <Label className="font-semibold">
                  关联商品<span className="text-red-500">*</span>
                </Label>
                <div className="space-y-4">
                  {productsInForm.map((product, index) => {
                    return (
                      <div
                        key={index}
                        className="border rounded-lg p-4 space-y-4 bg-gray-50/50"
                      >
                        <div className="flex-grow space-y-2">
                          <div className="flex justify-between items-center gap-4">
                            <Label>选择商品</Label>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-gray-500 hover:bg-red-100 hover:text-red-600"
                              onClick={() => handleDeleteProduct(index)}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                          <Select
                            value={product.product_id}
                            onValueChange={(newProdId) =>
                              handleProductChange(index, newProdId)
                            }
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="选择一个商品" />
                            </SelectTrigger>
                            <SelectContent>
                              {allProducts.map((p) => (
                                <SelectItem key={p.id} value={p.id}>
                                  {p.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="grid grid-cols-2 gap-4 items-end">
                          <div className="space-y-2">
                            <Label>原价 (元)</Label>
                            <Input
                              value={formatPriceToYuan(product.original_price)}
                              disabled
                              className="font-mono bg-gray-100"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>活动价 (元)</Label>
                            <Input
                              type="text"
                              placeholder="例如：199.99"
                              value={product.activity_price || ""}
                              onChange={(e) =>
                                handleProductFieldChange(
                                  index,
                                  "activity_price",
                                  e.target.value
                                )
                              }
                              className="font-mono"
                            />
                          </div>
                        </div>

                        <div className="space-y-3">
                          <div className="flex items-center space-x-2 mt-4">
                            <Label htmlFor={`gift-switch-${index}`}>
                              附加赠品
                            </Label>
                            <Switch
                              id={`gift-switch-${index}`}
                              checked={product._isGiftEnabled}
                              onCheckedChange={(checked) =>
                                handleProductFieldChange(
                                  index,
                                  "_isGiftEnabled",
                                  checked
                                )
                              }
                              className="data-[state=checked]:bg-blue-600 data-[state=unchecked]:bg-gray-300"
                              thumbClassName="bg-white"
                            />
                          </div>
                          {product._isGiftEnabled && (
                            <div className="grid grid-cols-2 gap-4 p-4 border rounded-md bg-white">
                              <div className="space-y-2">
                                <Label>赠品名称</Label>
                                <Input
                                  placeholder="例如：一年会员"
                                  value={product.gift_name || ""}
                                  onChange={(e) =>
                                    handleProductFieldChange(
                                      index,
                                      "gift_name",
                                      e.target.value
                                    )
                                  }
                                />
                              </div>
                              <div className="space-y-2">
                                <Label>赠品价格 (元)</Label>
                                <Input
                                  type="text"
                                  placeholder="例如：199.00"
                                  value={product.gift_price || ""}
                                  onChange={(e) =>
                                    handleProductFieldChange(
                                      index,
                                      "gift_price",
                                      e.target.value
                                    )
                                  }
                                  className="font-mono"
                                />
                              </div>

                              <div className="space-y-2">
                                <Label>赠品时长</Label>
                                <Input
                                  type="text"
                                  placeholder="例如：1"
                                  value={product.gift_duration || ""}
                                  onChange={(e) =>
                                    handleProductFieldChange(
                                      index,
                                      "gift_duration",
                                      e.target.value
                                    )
                                  }
                                />
                              </div>
                              <div className="space-y-2">
                                <Label>时长单位</Label>
                                <Select
                                  value={product.gift_unit}
                                  onValueChange={(val) =>
                                    handleProductFieldChange(
                                      index,
                                      "gift_unit",
                                      val
                                    )
                                  }
                                >
                                  <SelectTrigger>
                                    <SelectValue placeholder="选择单位" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {GIFT_UNIT_OPTIONS.map((opt) => (
                                      <SelectItem
                                        key={opt.value}
                                        value={opt.value}
                                      >
                                        {opt.label}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <Button
                  variant="outline"
                  className="w-full border-dashed"
                  onClick={onRelatedNewProduct}
                >
                  + 添加关联商品
                </Button>
              </div>
            </form>
            <DialogFooter>
              <DialogClose asChild>
                <Button variant="outline">取消</Button>
              </DialogClose>
              <Button onClick={() => formRef.current?.requestSubmit()}>
                确认
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {!loading && activities.length > 0 && (
        <div className="flex items-center justify-between mt-4">
          <div className="text-sm text-gray-500 whitespace-nowrap">
            共 {total} 条活动记录，第 {page} 页 / 共 {totalPages} 页
          </div>
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    setPage((p) => Math.max(1, p - 1));
                  }}
                />
              </PaginationItem>
              <PaginationItem>
                <PaginationLink href="#">{page}</PaginationLink>
              </PaginationItem>
              <PaginationItem>
                <PaginationNext
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    setPage((p) => Math.min(totalPages, p + 1));
                  }}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      )}
    </div>
  );
}
